// @client/hooks/useProfilesQuery.ts
// Profile Manager Query & Mutation Hooks - FIXED Relations Logic

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiClient } from "@client/lib/api-client";
import { useToast } from "@client/hooks/use-toast";

// Import all required types from @shared/types
import type { 
  ProfileFilters, 
  PaginationState, 
  SortingState,
  ProfileWithRelations,
  Profile,
  List,
  Category,
  User,
  ScoreRange,
  ProfileCategoryInfo,
  ProfileListInfo
} from "@shared/types";

// ==================== RESPONSE TYPES ====================

interface ProfileSearchResponse {
  profiles: ProfileWithRelations[];
  pagination: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    total: number;
    startIndex: number;
    endIndex: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  aggregations?: {
    scoreRanges: Array<{ range: string; count: number }>;
    companies: Array<{ company: string; count: number }>;
    categories: Array<{ category: string; count: number }>;
  };
}

interface BulkOperationRequest {
  mode: 'selected' | 'filtered';
  profileIds?: number[];
  filters?: ProfileFilters;
  pagination?: PaginationState;
  sorting?: SortingState;
  updates?: Record<string, any>;
  format?: 'csv' | 'excel';
  confirmed?: boolean;
  action?: string;
}

interface BulkOperationResult {
  success: boolean;
  affectedCount: number;
  errors?: Array<{ profileId: number; error: string }>;
  downloadUrl?: string;
  filename?: string;
}

interface DataValueOption {
  id: string;
  label: string;
  order: number;
  isActive: boolean;
}

const API_ENDPOINT_URL = ApiClient.getApiBase();

// ==================== HELPER: Build API Params ====================

function buildApiParams(
  filters: ProfileFilters,
  pagination: PaginationState,
  sorting: SortingState
): URLSearchParams {
  const params = new URLSearchParams();
  
  // Pagination
  params.set('page', pagination.page.toString());
  params.set('limit', pagination.limit.toString());
  
  // Sorting
  params.set('sortField', sorting.field);
  params.set('sortOrder', sorting.order);
  
  // Filters - only add if defined
  if (filters.search) params.set('search', filters.search);
  if (filters.listIds?.length) params.set('listIds', filters.listIds.join(','));  // ✅ NEU: Multi-List
  if (filters.listId && filters.listId !== 'all') params.set('listId', filters.listId.toString());  // Legacy
  if (filters.categoryIds?.length) params.set('categoryIds', filters.categoryIds.join(','));
  if (filters.scoreRange) params.set('scoreRange', filters.scoreRange);
  if (filters.createdBy) params.set('createdBy', filters.createdBy.toString());
  
  // Boolean filters - explicit false is different from undefined
  if (filters.hasCategories !== undefined) params.set('hasCategories', filters.hasCategories.toString());
  if (filters.hasLists !== undefined) params.set('hasLists', filters.hasLists.toString());
  if (filters.hasEmail !== undefined) params.set('hasEmail', filters.hasEmail.toString()); // ✅ NEU
  
  // Date filters
  if (filters.afterField) params.set('afterField', filters.afterField);
  if (filters.afterDate) params.set('afterDate', filters.afterDate);
  if (filters.beforeField) params.set('beforeField', filters.beforeField);
  if (filters.beforeDate) params.set('beforeDate', filters.beforeDate);
  
  // Marker filters
  if (filters.isExported !== undefined) params.set('isExported', filters.isExported.toString());
  if (filters.isActive !== undefined) params.set('isActive', filters.isActive.toString());
  if (filters.isBlocked !== undefined) params.set('isBlocked', filters.isBlocked.toString());
  if (filters.isRegistered !== undefined) params.set('isRegistered', filters.isRegistered.toString());
  
  return params;
}

// ==================== QUERY HOOKS ====================

/**
 * Main profile search query with eager-loaded relations
 * Returns ProfileWithRelations[] with categories and lists
 */
export function useProfilesQuery(
  filters: ProfileFilters,
  pagination: PaginationState,
  sorting: SortingState,
  options?: { enabled?: boolean }
) {
  const apiParams = buildApiParams(filters, pagination, sorting);
  
  return useQuery({
    queryKey: ['profiles', 'list', filters, pagination, sorting],
    queryFn: async () => {
      const url = `/profiles?${apiParams.toString()}`;
      const response = await ApiClient.get<ProfileSearchResponse>(url);
      return response;
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: options?.enabled !== false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Single profile by ID (for detail page)
 */
export function useProfileQuery(profileId: number) {
  return useQuery({
    queryKey: ['profiles', 'detail', profileId],
    queryFn: () => ApiClient.get<ProfileWithRelations>(`/profiles/${profileId}`),
    enabled: profileId > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

// ==================== STATIC DATA HOOKS ====================

/**
 * Fetch all lists
 */
export function useListsQuery() {
  return useQuery({
    queryKey: ['lists'],
    queryFn: () => ApiClient.get<{ lists: List[] }>('/lists'),
    staleTime: 10 * 60 * 1000,
    select: (data) => data?.lists || [],
  });
}

/**
 * Fetch all categories (Core Category type)
 */
export function useCategoriesQuery() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => ApiClient.get<{ categories: Category[] }>('/profile-categories'),
    staleTime: 10 * 60 * 1000,
    select: (data) => data?.categories || [],
  });
}

/**
 * Fetch all users
 */
export function useUsersQuery() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => ApiClient.get<{ users: User[] }>('/users'),
    staleTime: 10 * 60 * 1000,
    select: (data) => data?.users || [],
  });
}

/**
 * Fetch score ranges from settings
 */
export function useScoreRangesQuery() {
  return useQuery({
    queryKey: ['score-ranges'],
    queryFn: () => ApiClient.get<ScoreRange[]>('/score-ranges'),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Data values for quick-edit popovers (Gender, Industry)
 */
export function useDataValuesQuery(type: 'gender' | 'industry') {
  return useQuery({
    queryKey: ['data-values', type],
    queryFn: () => ApiClient.get<DataValueOption[]>(`/settings/${type}`),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Combined static data hook - fetches all reference data at once
 */
export function useStaticDataQueries() {
  const listsQuery = useListsQuery();
  const categoriesQuery = useCategoriesQuery();
  const usersQuery = useUsersQuery();
  const scoreRangesQuery = useScoreRangesQuery();
  
  return {
    lists: listsQuery.data || [],
    categories: categoriesQuery.data || [],
    users: usersQuery.data || [],
    scoreRanges: scoreRangesQuery.data || [],
    isLoading: listsQuery.isLoading || categoriesQuery.isLoading || 
                usersQuery.isLoading || scoreRangesQuery.isLoading,
    isError: listsQuery.isError || categoriesQuery.isError || 
              usersQuery.isError || scoreRangesQuery.isError,
  };
}

// ==================== MUTATION HOOKS ====================

/**
 * Single profile update (for inline edits like score, gender, industry)
 */
export function useProfileUpdateMutation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ 
      profileId, 
      updates 
    }: { 
      profileId: number; 
      updates: Partial<Profile> 
    }) => {
      return ApiClient.put<Profile>(`/profiles/${profileId}`, updates);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['profiles', 'detail', variables.profileId], data);
      queryClient.invalidateQueries({ queryKey: ['profiles', 'list'] });
      
      toast({
        title: "Profile updated",
        description: "Changes saved successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error?.message || "Please try again",
        variant: "destructive",
      });
    },
  });
}

/**
 * ✅ FIXED: Profile Relations Mutation (Categories + Lists)
 * Uses V2.0 Relations API with individual POST/DELETE calls
 */
export function useProfileRelationsMutation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ 
      profileId, 
      type, 
      oldIds,
      newIds
    }: { 
      profileId: number; 
      type: 'categories' | 'lists'; 
      oldIds: number[];
      newIds: number[];
    }) => {
      console.log('[Relations Mutation] Starting:', {
        profileId,
        type,
        oldIds,
        newIds
      });
      
      // Calculate diff
      const toRemove = oldIds.filter(id => !newIds.includes(id));
      const toAdd = newIds.filter(id => !oldIds.includes(id));
      
      console.log('[Relations Mutation] Diff calculated:', {
        toRemove,
        toAdd
      });
      
      // Base endpoint
      const baseEndpoint = `/profiles/${profileId}/${type}`;
      
      // ✅ REMOVE OLD RELATIONS (individual DELETE calls)
      for (const id of toRemove) {
        console.log(`[Relations Mutation] Removing ${type}:`, id);
        await ApiClient.delete(`${baseEndpoint}/${id}`);
      }
      
      // ✅ ADD NEW RELATIONS (individual POST calls)
      for (const id of toAdd) {
        console.log(`[Relations Mutation] Adding ${type}:`, id);
        
        const body = type === 'categories' 
          ? { categoryId: id }
          : { listId: id };
        
        await ApiClient.post(baseEndpoint, body);
      }
      
      console.log('[Relations Mutation] Success');
      
      return {
        success: true,
        removed: toRemove.length,
        added: toAdd.length
      };
    },
    onSuccess: (data, variables) => {
      console.log('[Relations Mutation] onSuccess:', data);
      
      // Invalidate all profile queries to refetch with new relations
      queryClient.invalidateQueries({ queryKey: ['profiles', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['profiles', 'detail', variables.profileId] });
      
      const typeName = variables.type === 'categories' ? 'Categories' : 'Lists';
      toast({
        title: `${typeName} updated`,
        description: `${data.removed} removed, ${data.added} added`,
      });
    },
    onError: (error: any, variables) => {
      console.error('[Relations Mutation] Error:', error);
      
      const typeName = variables.type === 'categories' ? 'Categories' : 'Lists';
      toast({
        title: `${typeName} update failed`,
        description: error?.message || "Please try again",
        variant: "destructive",
      });
    },
  });
}

/**
 * Bulk operations (update, delete, export)
 */
export function useBulkOperationMutation(operation: 'update' | 'delete' | 'export' | 'action') {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (request: BulkOperationRequest) => {
      console.log('[Bulk Operation] Starting:', operation, request);
      
      const endpoint = `/profiles/bulk/${operation}`;
      
      // KRITISCH: Export braucht spezielles Handling für File Download
      if (operation === 'export') {
        return await handleExportDownload(endpoint, request);
      }

      if (operation === 'action') {
        const resolvedAction = request.action || "generate-gender";
        const actionParams = resolvedAction.split("-");
        const endpoint = `/profiles/batch/${actionParams[1]}`;
        return await handleAction(endpoint, request);
      }
      
      // Standard bulk operations (update/delete)
      let apiParams: URLSearchParams | undefined;
      if (request.mode === 'filtered') {
        apiParams = buildApiParams(
          request.filters!,
          request.pagination!,
          request.sorting!
        );
        request.filters = Object.fromEntries(apiParams.entries()) as any;
      }
      
      const response = await ApiClient.post<BulkOperationResult>(endpoint, request);
      return response;
    },
    onSuccess: (data) => {
      if (operation !== 'export') {
        queryClient.invalidateQueries({ queryKey: ['profiles'] });
      }
      
      const messages = {
        update: `Updated ${data.affectedCount} profiles`,
        delete: `Deleted ${data.affectedCount} profiles`,
        export: `Exported ${data.affectedCount} profiles`,
        action: `Processed ${data.affectedCount} profiles`,
      };
      
      toast({
        title: "Operation successful",
        description: messages[operation],
      });
    },
    onError: (error: any) => {
      console.error('[Bulk Operation] Error:', operation, error);
      toast({
        title: "Operation failed",
        description: error?.message || "Please try again",
        variant: "destructive",
      });
    },
  });
}

async function handleAction(
  endpoint: string, 
  request: BulkOperationRequest
): Promise<BulkOperationResult> {
  console.log('[Action] Starting ...', endpoint, request);
  
  try {
    const response = await ApiClient.post<BulkOperationResult>(endpoint, request);
    return response;

  } catch (error) {
    console.error('[Export] Download error:', error);
    throw error;
  }
}

// ==========================================
// HELPER: Export File Download Handler
// ==========================================

async function handleExportDownload(
  endpoint: string, 
  request: BulkOperationRequest
): Promise<BulkOperationResult> {
  console.log('[Export] Starting file download...', endpoint, request);
  
  try {
    // Build API params for filtered mode
    let apiParams: URLSearchParams | undefined;
    if (request.mode === 'filtered') {
      apiParams = buildApiParams(
        request.filters!,
        request.pagination!,
        request.sorting!
      );
      request.filters = Object.fromEntries(apiParams.entries()) as any;
    }

    // KRITISCH: Fetch mit Blob-Response statt JSON
    const response = await fetch(`${API_ENDPOINT_URL}${endpoint}`, {
      method: 'POST',
      credentials: 'include', // Session cookies
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request)
    });

    console.log('[Export] Response status:', response.status);
    console.log('[Export] Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      // Error response ist JSON
      const errorData = await response.json();
      console.error('[Export] Error response:', errorData);
      throw new Error(errorData.error || 'Export failed');
    }

    // KRITISCH: Response als Blob behandeln für File Download
    const blob = await response.blob();
    console.log('[Export] Blob received:', blob.size, 'bytes, type:', blob.type);

    // Filename aus Content-Disposition Header extrahieren
    const contentDisposition = response.headers.get('Content-Disposition');
    console.log('[Export] Content-Disposition:', contentDisposition);
    
    let filename = `profiles_export_${new Date().toISOString().slice(0, 10)}.csv`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    console.log('[Export] Download filename:', filename);

    // Blob als Download anbieten
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    
    document.body.appendChild(a);
    console.log('[Export] Triggering download...');
    a.click();
    
    // Cleanup
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      console.log('[Export] Download cleanup complete');
    }, 100);

    // Return success result (affectedCount wird aus Blob nicht extrahiert)
    // Alternative: Backend könnte affectedCount in X-Header senden
    return {
      success: true,
      affectedCount: request.profileIds?.length || 0,
      message: `Export successful: ${filename}`,
      filename
    } as BulkOperationResult;

  } catch (error) {
    console.error('[Export] Download error:', error);
    throw error;
  }
}

/**
 * Combined mutations hook for convenience
 */
export function useProfileMutations() {
  const profileUpdate = useProfileUpdateMutation();
  const relations = useProfileRelationsMutation();
  const bulkAction = useBulkOperationMutation('action');
  const bulkUpdate = useBulkOperationMutation('update');
  const bulkDelete = useBulkOperationMutation('delete');
  const bulkExport = useBulkOperationMutation('export');
  
  return {
    profileUpdate,
    relations,
    bulkAction,
    bulkUpdate,
    bulkDelete,
    bulkExport,
    
    isLoading: profileUpdate.isPending || relations.isPending ||
               bulkUpdate.isPending || bulkDelete.isPending || bulkExport.isPending,
  };
}

// ==================== CACHE UTILITIES ====================

/**
 * Helper hook for manual cache invalidation
 */
export function useInvalidateProfileCaches() {
  const queryClient = useQueryClient();
  
  return {
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
    
    invalidateProfiles: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles', 'list'] });
    },
    
    invalidateProfile: (profileId: number) => {
      queryClient.invalidateQueries({ queryKey: ['profiles', 'detail', profileId] });
    },
  };
}